import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/ratgeber/shopify-oder-wix";
const TITLE = "Shopify oder Wix: welcher Shop passt zu Ihrem Vorhaben?";
const DESCRIPTION =
  "Shopify oder Wix im sachlichen Vergleich: Kosten, Sortimentsgröße, Zahlungsarten, Erweiterbarkeit und Grenzen. Mit klarer Empfehlung je nach Ausgangslage.";
const UPDATED = "2026-09-12";

const ANSWER =
  "Wix ist günstig und schnell aufgesetzt und reicht für kleine Sortimente, bei denen der Shop eine Ergänzung zur Website ist. Shopify ist die stärkere Wahl, sobald Verkauf das Hauptgeschäft ist: mehr Produkte und Varianten, belastbare Zahlungs- und Versandlogik, ein sehr großes App-Angebot und Anpassbarkeit über den Theme-Code.";

const faqs = [
  {
    q: "Ist Wix günstiger als Shopify?",
    a: "In den monatlichen Grundkosten meist ja. Der Unterschied relativiert sich, sobald Funktionen über Apps ergänzt werden oder das Sortiment wächst, weil dann Aufwand statt Tarif den Preis bestimmt.",
  },
  {
    q: "Wann reicht Wix aus?",
    a: "Bei wenigen Produkten, einfacher Versandlogik und dem Shop als Zusatz zu einer Inhaltsseite. Wenn das Sortiment überschaubar bleibt, ist der Funktionsumfang selten das Problem.",
  },
  {
    q: "Wann ist Shopify die bessere Wahl?",
    a: "Sobald Verkauf das Kerngeschäft ist: viele Varianten, mehrere Länder und Währungen, Anbindung an Warenwirtschaft, Abos oder Bundles, und individuelle Anpassungen im Theme.",
  },
  {
    q: "Kann ich später von Wix zu Shopify wechseln?",
    a: "Ja. Produkte und Inhalte lassen sich übernehmen, entscheidend ist ein sauberer Weiterleitungsplan für alle alten Adressen, damit Sichtbarkeit erhalten bleibt.",
  },
  {
    q: "Was kostet ein Shopify-Shop im Aufbau?",
    a: "Je nach Umfang bewegen sich saubere Projekte üblicherweise im vierstelligen Bereich. Ich nenne nach einem Gespräch einen festen Rahmen statt einer offenen Stundenschätzung.",
  },
];

export const Route = createFileRoute("/ratgeber/shopify-oder-wix")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({
      title: TITLE,
      description: DESCRIPTION,
      path: PATH,
      faqs,
      section: "Shopsysteme",
      updated: UPDATED,
    }),
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Ratgeber · Vergleich"
      title="Shopify oder Wix: die Entscheidung hängt am Sortiment"
      intro="Beide Systeme bauen einen Onlineshop. Der Unterschied zeigt sich erst, wenn Produkte, Varianten und Prozesse wachsen. Dieser Vergleich ordnet ein, ab wann welches System das passende ist."
      source="ratgeber-shopify-oder-wix"
      answer={ANSWER}
      updated={UPDATED}
      sections={[
        {
          h: "Wo Wix stark ist",
          p: [
            "Wix ist auf Websites ausgelegt, bei denen ein Shop dazukommt. Die Bedienung ist visuell, der Einstieg schnell, und für wenige Produkte mit einfacher Versandlogik reicht der Funktionsumfang.",
            "Wer eine Seite pflegt, in der Inhalt im Vordergrund steht und der Verkauf eine Nebenrolle spielt, kommt damit lange aus.",
          ],
        },
        {
          h: "Wo Shopify stark ist",
          p: [
            "Shopify ist von Grund auf für Verkauf gebaut. Varianten, Lagerbestände, Versandprofile, mehrere Länder, Steuern und Zahlungsarten sind keine Zusatzfunktion, sondern Kern des Systems.",
          ],
          list: [
            "Große Sortimente mit vielen Varianten und Attributen",
            "Verkauf in mehreren Ländern und Währungen",
            "Anbindung an Warenwirtschaft, Buchhaltung und Versanddienstleister",
            "Abos, Bundles und individuelle Preislogik",
            "Freie Anpassung über den Theme-Code statt nur über Bausteine",
          ],
        },
        {
          h: "Kosten realistisch betrachten",
          p: [
            "Der Tarifvergleich allein führt in die Irre. Entscheidend ist, was Sie an Apps, Anpassungen und Arbeitszeit brauchen, um Ihre Abläufe abzubilden.",
            "Ein System, das an einer Stelle nicht mitkommt, kostet in Umwegen und Handarbeit oft mehr als die Differenz im Monatstarif.",
          ],
        },
        {
          h: "Wechsel von Wix zu Shopify",
          p: [
            "Ein Umzug ist üblich, sobald der Shop wächst. Produkte, Kunden und Inhalte lassen sich übernehmen. Der kritische Teil ist der Weiterleitungsplan: Jede alte Adresse braucht ein passendes neues Ziel, sonst verlieren Sie Rankings.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/ratgeber/wix-zu-shopify", label: "Wix zu Shopify wechseln" },
        { to: "/ratgeber/shopify-vs-woocommerce", label: "Shopify vs. WooCommerce" },
        { to: "/shopify-shop-erstellen-lassen", label: "Shop erstellen lassen" },
        { to: "/ratgeber/shopify-kosten", label: "Shopify Kosten im Detail" },
        { to: "/kontakt", label: "Situation besprechen" },
      ]}
    />
  );
}
